import { jsxs, jsx } from 'react/jsx-runtime';
import { useState, useContext, useRef, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@skatteetaten/ds-buttons';
import { dsI18n } from '@skatteetaten/ds-core-utils';
import { Checkbox } from '@skatteetaten/ds-forms';
import { PersonSVGpath } from '@skatteetaten/ds-icons';
import { Heading } from '@skatteetaten/ds-typography';
import { RolePickerContext } from '../RolePickerContext/index.esm.js';
import { RolePickerRow } from '../RolePickerRow/index.esm.js';
import '@skatteetaten/ds-navigation';
import '@skatteetaten/ds-status';
import './styles.css';


var styles = {
    "peopleList": "RolePickerPeopleList-module_peopleList__dEumR",
    "showDeceasedChecbox": "RolePickerPeopleList-module_showDeceasedChecbox__4SCg3",
    "showAllButtonWrapper": "RolePickerPeopleList-module_showAllButtonWrapper__-ErxO"
};

const MAX_INIITAL_ITEMS = 5;
const RolePickerPeopleList = ({ people, filterQuery, showDeceasedPeople: showDeceasedPeopleExternal })=>{
    const { t } = useTranslation('ds_overlays', {
        i18n: dsI18n
    });
    const [showAll, setShowAll] = useState(false);
    const [showDeceasedPeople, setShowDeceasedPeople] = useState(showDeceasedPeopleExternal);
    const ctx = useContext(RolePickerContext);
    const navRef = useRef(null);
    const handleShowAll = ()=>{
        setShowAll(true);
        navRef.current?.querySelectorAll('a')[MAX_INIITAL_ITEMS - 1].focus();
    };
    const handleEntityClicked = async (entity)=>{
        ctx?.setLoadingEntityId(entity.personId);
        ctx?.onEntitySelect?.(entity).then((res)=>{
            if (res?.error) {
                ctx?.setError({
                    entityId: entity.personId,
                    message: res.error
                });
            }
            ctx.setLoadingEntityId(undefined);
        });
    };
    const visibleItems = useMemo(()=>{
        // lager en dyp kopiering av person-listen for å unngå mutasjon
        let items = JSON.parse(JSON.stringify(people.list));
        items = !showDeceasedPeople ? items.filter((p)=>!p.isDeleted) : items;
        if (filterQuery) {
            return items.filter((f)=>f.name.toLowerCase().includes(filterQuery.toLowerCase()) || f.personId.includes(filterQuery.toLowerCase()));
        }
        if (showAll) {
            return items;
        }
        return items.slice(0, MAX_INIITAL_ITEMS);
    }, [
        people.list,
        showDeceasedPeople,
        filterQuery,
        showAll
    ]);
    const getShowAllCount = useCallback(()=>{
        return showDeceasedPeople ? people.total : people.list.filter((p)=>!p.isDeleted).length;
    }, [
        people.list,
        people.total,
        showDeceasedPeople
    ]);
    const displayShowAllButton = !showAll && !filterQuery && people.total > MAX_INIITAL_ITEMS;
    return /*#__PURE__*/ jsxs("div", {
        children: [
            /*#__PURE__*/ jsx(Heading, {
                as: 'h2',
                level: 3,
                id: 'otherPeopleHeadingId',
                children: filterQuery ? `${visibleItems.length} ${t('rolepicker.PeopleHits')}` : t('rolepicker.PeopleHeading')
            }),
            people.list.some((person)=>person.isDeleted) ? /*#__PURE__*/ jsx(Checkbox, {
                className: styles.showDeceasedChecbox,
                checked: showDeceasedPeople,
                onChange: (e)=>setShowDeceasedPeople(e.target.checked),
                children: t('rolepicker.ShowDeceasedPersons')
            }) : null,
            /*#__PURE__*/ jsx("nav", {
                ref: navRef,
                "aria-labelledby": 'otherPeopleHeadingId',
                children: /*#__PURE__*/ jsx("ul", {
                    className: styles.peopleList,
                    children: visibleItems.map((item)=>{
                        return /*#__PURE__*/ jsx("li", {
                            children: /*#__PURE__*/ jsx(RolePickerRow, {
                                id: item.personId,
                                title: `${item.name}${item.isDeleted ? ` (${t('rolepicker.Deceased')})` : ''}`,
                                description: `${t('rolepicker.PeopleDescriptionPrefix')} ${item.personId}`,
                                svgPath: PersonSVGpath,
                                onClick: ()=>handleEntityClicked(item)
                            })
                        }, item.personId);
                    })
                })
            }),
            displayShowAllButton ? /*#__PURE__*/ jsx("div", {
                className: styles.showAllButtonWrapper,
                children: /*#__PURE__*/ jsx(Button, {
                    variant: 'tertiary',
                    onClick: handleShowAll,
                    children: `${t('rolepicker.ShowAll')} ${t('rolepicker.People')} (${getShowAllCount()})`
                })
            }) : null
        ]
    });
};

export { RolePickerPeopleList };
